<template>
  <div class="search-results">
    <h1 class="text-h4 mb-2">{{ t('app.search') }}</h1>
    
    <div class="text-body-1 mb-4">
      <span>{{ t('app.resultsFor') }}</span>
      <span class="font-weight-bold">{{ query }}</span>
    </div>
    
    <v-skeleton-loader
      v-if="carregando"
      type="card"
      class="mt-4"
    ></v-skeleton-loader>
    
    <template v-else>
      <!-- Resumo dos resultados -->
      <v-alert
        v-if="totalResultados > 0"
        type="info"
        variant="tonal"
        class="mb-4"
      >
        Encontrados {{ totalResultados }} resultados em {{ Object.keys(tiposResultados).filter(tipo => tiposResultados[tipo]).length }} categorias
      </v-alert>
      
      <!-- Sem resultados -->
      <v-card v-if="totalResultados === 0" class="pa-4 text-center">
        <v-icon size="64" color="grey" class="mb-3">mdi-magnify-close</v-icon>
        <div class="text-h6 mb-2">{{ t('app.noData') }}</div>
        <div class="text-body-2 mb-4">
          Não conseguimos encontrar resultados para "{{ query }}". Verifique se você digitou corretamente ou tente termos mais genéricos.
        </div>
        <v-row justify="center">
          <v-col cols="12" sm="8" md="6">
            <v-card variant="outlined" class="pa-3 mb-3">
              <div class="text-subtitle-1 mb-2">{{ t('app.searchTips') }}</div>
              <ul class="text-left">
                <li>Para blocos, use o número do bloco (ex: 12345)</li>
                <li>Para transações, use o hash completo da transação</li>
                <li>Para contas, use o endereço completo</li>
                <li>Para tokens, use o símbolo ou nome do token</li>
              </ul>
            </v-card>
            <v-btn
              color="primary"
              prepend-icon="mdi-home"
              :to="{ name: 'dashboard' }"
            >
              Voltar para o Dashboard
            </v-btn>
          </v-col>
        </v-row>
      </v-card>
      
      <!-- Resultados de Blocos -->
      <v-expansion-panels
        v-if="resultados.blocos.length > 0"
        variant="accordion"
        class="mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-cube-outline</v-icon>
              <span>Blocos ({{ resultados.blocos.length }})</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list>
              <v-list-item
                v-for="bloco in resultados.blocos"
                :key="bloco.number"
                :to="{ name: 'block-details', params: { id: bloco.number } }"
              >
                <template v-slot:prepend>
                  <v-avatar
                    color="primary"
                    size="36"
                    class="mr-3"
                  >
                    <span class="text-caption font-weight-bold text-white">B</span>
                  </v-avatar>
                </template>
                <v-list-item-title>
                  Bloco #{{ bloco.number }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ bloco.transactionCount }} transações | {{ formatarData(bloco.timestamp) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      
      <!-- Resultados de Transações -->
      <v-expansion-panels
        v-if="resultados.transacoes.length > 0"
        variant="accordion"
        class="mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-swap-horizontal</v-icon>
              <span>Transações ({{ resultados.transacoes.length }})</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list>
              <v-list-item
                v-for="transacao in resultados.transacoes"
                :key="transacao.hash"
                :to="{ name: 'transaction-details', params: { hash: transacao.hash } }"
              >
                <template v-slot:prepend>
                  <v-avatar
                    :color="transacao.status === 'confirmed' ? 'success' : transacao.status === 'pending' ? 'warning' : 'error'"
                    size="36"
                    class="mr-3"
                  >
                    <span class="text-caption font-weight-bold text-white">Tx</span>
                  </v-avatar>
                </template>
                <v-list-item-title class="text-truncate">
                  {{ transacao.hash }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  De: {{ truncarEndereco(transacao.from) }} | Para: {{ truncarEndereco(transacao.to) }} | {{ formatarValorToken(transacao.value) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      
      <!-- Resultados de Contas -->
      <v-expansion-panels
        v-if="resultados.contas.length > 0"
        variant="accordion"
        class="mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-account-outline</v-icon>
              <span>Contas ({{ resultados.contas.length }})</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list>
              <v-list-item
                v-for="conta in resultados.contas"
                :key="conta.address"
                :to="{ name: 'account-details', params: { address: conta.address } }"
              >
                <template v-slot:prepend>
                  <v-avatar
                    :color="conta.tipo === 'contrato' ? 'primary' : 'secondary'"
                    size="36"
                    class="mr-3"
                  >
                    <v-icon size="small" color="white">
                      {{ conta.tipo === 'contrato' ? 'mdi-file-document-outline' : 'mdi-account-outline' }}
                    </v-icon>
                  </v-avatar>
                </template>
                <v-list-item-title class="text-truncate">
                  {{ conta.address }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ conta.tipo === 'contrato' ? 'Contrato' : 'Conta' }} | Saldo: {{ formatarValorToken(conta.saldo) }} | Transações: {{ conta.transacoes }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      
      <!-- Resultados de Validadores -->
      <v-expansion-panels
        v-if="resultados.validadores.length > 0"
        variant="accordion"
        class="mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-shield-check-outline</v-icon>
              <span>Validadores ({{ resultados.validadores.length }})</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list>
              <v-list-item
                v-for="validador in resultados.validadores"
                :key="validador.address"
                :to="{ name: 'validator-details', params: { id: validador.address } }"
              >
                <template v-slot:prepend>
                  <v-avatar
                    :color="validador.status === 'active' ? 'success' : 'grey'"
                    size="36"
                    class="mr-3"
                  >
                    <span class="text-caption font-weight-bold text-white">V</span>
                  </v-avatar>
                </template>
                <v-list-item-title>
                  {{ validador.name }}
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ validador.address.substring(0, 10) }}...{{ validador.address.substring(validador.address.length - 8) }} | Stake: {{ formatarValorToken(validador.stake) }} | Mérito: {{ validador.merit }}/100
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
      
      <!-- Resultados de Tokens -->
      <v-expansion-panels
        v-if="resultados.tokens.length > 0"
        variant="accordion"
        class="mb-4"
      >
        <v-expansion-panel>
          <v-expansion-panel-title>
            <div class="d-flex align-center">
              <v-icon class="mr-2" color="primary">mdi-currency-token</v-icon>
              <span>Tokens ({{ resultados.tokens.length }})</span>
            </div>
          </v-expansion-panel-title>
          <v-expansion-panel-text>
            <v-list>
              <v-list-item
                v-for="token in resultados.tokens"
                :key="token.address"
                :to="{ name: 'account-details', params: { address: token.address } }"
              >
                <template v-slot:prepend>
                  <v-avatar
                    color="primary"
                    size="36"
                    class="mr-3"
                  >
                    <span class="text-caption font-weight-bold text-white">{{ token.symbol.charAt(0) }}</span>
                  </v-avatar>
                </template>
                <v-list-item-title>
                  {{ token.name }} ({{ token.symbol }})
                </v-list-item-title>
                <v-list-item-subtitle>
                  Suprimento Total: {{ formatarNumero(token.totalSupply) }} | Holders: {{ formatarNumero(token.holders) }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-expansion-panel-text>
        </v-expansion-panel>
      </v-expansion-panels>
    </template>
  </div>
</template>

<script>
import { useBlockchainStore } from '@/stores/blockchain'
import { useDeveloperStore } from '@/stores/developer'
import { formatarData, formatarValorToken, formatarNumero } from '@/services/utilidades'

export default {
  name: 'SearchResultsView',
  props: {
    query: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      carregando: true,
      resultados: {
        blocos: [],
        transacoes: [],
        contas: [],
        validadores: [],
        tokens: []
      }
    }
  },
  computed: {
    blockchainStore() {
      return useBlockchainStore()
    },
    
    developerStore() {
      return useDeveloperStore()
    },
    
    tiposResultados() {
      return {
        blocos: this.resultados.blocos.length > 0,
        transacoes: this.resultados.transacoes.length > 0,
        contas: this.resultados.contas.length > 0,
        validadores: this.resultados.validadores.length > 0,
        tokens: this.resultados.tokens.length > 0
      }
    },
    
    totalResultados() {
      return (
        this.resultados.blocos.length +
        this.resultados.transacoes.length +
        this.resultados.contas.length +
        this.resultados.validadores.length +
        this.resultados.tokens.length
      )
    }
  },
  methods: {
    formatarData,
    formatarValorToken,
    formatarNumero,
    
    truncarEndereco(endereco) {
      if (!endereco) return ''
      return endereco.substring(0, 8) + '...' + endereco.substring(endereco.length - 6)
    },
    
    async buscar() {
      this.carregando = true
      
      try {
        if (!this.query || this.query.trim() === '') {
          return
        }
        
        const termoBusca = this.query.trim()
        
        // Buscar dados reais via ExplorerAPI
        const { ExplorerAPI } = await import('@/services/api')
        const query = termoBusca
        
        // Try as block number
        if (/^\d+$/.test(query)) {
          try {
            const block = await ExplorerAPI.blocos.obterPorNumero(parseInt(query))
            if (block) {
              this.resultados.blocos.push(block)
            }
          } catch (_) {}
        }
        
        // Try as transaction hash (hex, 16+ chars)
        if (/^[0-9a-fA-F]{16,}$/.test(query)) {
          try {
            const tx = await ExplorerAPI.transacoes.obterPorHash(query)
            if (tx) {
              this.resultados.transacoes.push(tx)
            }
          } catch (_) {}
          
          // Also try as validator/account address
          try {
            const validators = await ExplorerAPI.validadores.obterTodos()
            const match = validators.find(v => 
              (v.address || v.endereco || '').includes(query.substring(0, 16))
            )
            if (match) {
              this.resultados.validadores.push(match)
            }
          } catch (_) {}
        }
        
        // Try as contract address
        try {
          const contract = await ExplorerAPI.contratos.obterPorEndereco(query)
          if (contract && !contract.error) {
            this.resultados.contas.push({
              address: contract.address || query,
              tipo: 'contrato',
              saldo: 0,
              transacoes: 0
            })
          }
        } catch (_) {}
        
        // Busca por validadores no store
        await this.blockchainStore.fetchValidators()
        const termoBuscaLower = termoBusca.toLowerCase()
        this.resultados.validadores = [
          ...this.resultados.validadores,
          ...this.blockchainStore.validators.filter(validador => 
            validador.name.toLowerCase().includes(termoBuscaLower) || 
            validador.address.toLowerCase().includes(termoBuscaLower)
          )
        ]
        
        // Deduplicate validators by address
        const seen = new Set()
        this.resultados.validadores = this.resultados.validadores.filter(v => {
          const key = v.address || v.endereco
          if (seen.has(key)) return false
          seen.add(key)
          return true
        })
        
        // Busca por tokens
        await this.developerStore.fetchTokens()
        this.resultados.tokens = this.developerStore.tokens.filter(token => 
          token.name.toLowerCase().includes(termoBuscaLower) || 
          token.symbol.toLowerCase().includes(termoBuscaLower) || 
          token.address.toLowerCase().includes(termoBuscaLower)
        )
      } catch (error) {
        console.error('Erro na busca:', error)
      } finally {
        this.carregando = false
      }
    }
  },
  watch: {
    query: {
      immediate: true,
      handler() {
        // Limpa os resultados anteriores
        this.resultados = {
          blocos: [],
          transacoes: [],
          contas: [],
          validadores: [],
          tokens: []
        }
        
        this.buscar()
      }
    }
  }
}
</script>

<style scoped>
.search-results {
  padding-bottom: 32px;
}
</style>
